import * as THREE from 'three';

export default class Pyramid {
    constructor(scene, physics, position = { x: 0, y: 0, z: 0 }, options = {}) {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        
        //console.log('Creating pyramid at position:', position);
        
        // Pyramid properties
        this.width = options.width || 3;
        this.height = options.height || 4;
        this.color = options.color || 0xCCBB88; // Sandy pyramid color by default
        this.mesh = null;
        this.collider = null;
        
        // Create the pyramid visual
        this.create();
    }
    
    create() {
        //console.log(`Creating pyramid mesh with width: ${this.width}, height: ${this.height}`);
        
        // Create a pyramid using a cone geometry
        const geometry = new THREE.ConeGeometry(this.width / 2, this.height, 4);
        const material = new THREE.MeshStandardMaterial({ 
            color: this.color,
            metalness: 0.1,
            roughness: 0.8,
            flatShading: true
        });
        
        this.mesh = new THREE.Mesh(geometry, material);
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.mesh.position.set(this.position.x, terrainY + this.position.y + (this.height / 2), this.position.z);
        
        // Enable shadows
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        
        // Add to scene
        this.scene.add(this.mesh);
        
        // Create physics for the pyramid
        if (this.physics && this.physics.isInitialized) {
            this.createPhysics();
        }
        
        // Add some detail to the pyramid
        this.addDetails();
    }
    
    createPhysics() {
        //console.log('Setting up pyramid physics');
        
        // Create a simple box collider for the pyramid
        const halfWidth = this.width / 2;
        const halfHeight = this.height / 2;
        
        // Create a THREE.Vector3 for the half extents
        const halfExtents = new THREE.Vector3(halfWidth, halfHeight, halfWidth);
        
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.collider = this.physics.createStaticBox(
            new THREE.Vector3(this.position.x, terrainY + this.position.y + halfHeight, this.position.z),
            halfExtents,
            0.5 // Friction
        );
    }
    
    addDetails() {
        // Add some texture or details to the pyramid
        // For example, we could add some lines to represent the edges of the blocks
        
        // Create a slightly darker material for the details
        const detailColor = new THREE.Color(this.color).multiplyScalar(0.8);
        const detailMaterial = new THREE.LineBasicMaterial({ 
            color: detailColor,
            linewidth: 1
        });
        
        // Create horizontal lines to represent layers of the pyramid
        const numLayers = 5;
        const layerHeight = this.height / numLayers;
        
        for (let i = 1; i < numLayers; i++) {
            const y = (i * layerHeight);
            const radius = (1 - (i / numLayers)) * (this.width / 2);
            
            // Create points for a square at this layer
            const points = [];
            for (let j = 0; j < 4; j++) {
                const angle = (j / 4) * Math.PI * 2;
                points.push(new THREE.Vector3(
                    Math.cos(angle) * radius,
                    y - (this.height / 2),
                    Math.sin(angle) * radius
                ));
            }
            // Close the loop
            points.push(points[0].clone());
            
            const layerGeometry = new THREE.BufferGeometry().setFromPoints(points);
            const layer = new THREE.Line(layerGeometry, detailMaterial);
            
            this.mesh.add(layer);
        }
    }
    
    update(deltaTime) {
        // No animation needed for the pyramid, but we keep the method
        // for consistency with other entities
    }
    
    remove() {
        // Remove from scene
        if (this.mesh) {
            this.scene.remove(this.mesh);
            this.mesh.geometry.dispose();
            this.mesh.material.dispose();
            this.mesh = null;
        }
        
        // Remove physics collider
        if (this.collider && this.physics) {
            this.physics.removeCollider(this.collider);
            this.collider = null;
        }
    }
}
